import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import BaseSingletonEntity from "%/dynamic-component-editor/classes/base-singleton-entity";
import BaseEntity from "%/dynamic-component-editor/classes/BaseEntity";
import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import Theme from "%/dynamic-component-editor/classes/theme";
import { STORY_TYPE_METADATA_FIELDS, allMetadataFields } from "%/dynamic-components/render-engine/constants/story-type-metadata-fields";
import {
  bareRequiredContextEntityType,
  isPrimitiveRequiredContext,
} from "%/dynamic-component-editor/helpers/required-context-entity-type";
import { applyThemeMotionForThemeId } from "%/dynamic-component-editor/helpers/apply-theme-motion";
import ProvidesContextEntry from "%/dynamic-component-editor/classes/provides-context-entry";
import ContextSourceOptions from "%/dynamic-component-editor/classes/context-source-options";
import { Context } from "%/dynamic-component-editor/classes/context";
import { bindingStampFor, buildVariableBindingIndex } from "%/dynamic-component-editor/helpers/variable-binding-index";
import { TRIGGERED_EVENT_KEY, TRIGGER_EVENT_ID, TRIGGER_ON_EVENT } from "@bloglovin/vue-component-library/animation";
import { useStyleStore } from "@vcl";
import { watch } from "vue";
import App, { TEMP_PK_RESOLVED_EVENT } from "@bloglovin/inspire-engine/App";
import CustomDynamicPage from "%/dynamic-component-editor/classes/custom-dynamic-page";
import CustomDynamicComponent from "%/dynamic-component-editor/classes/custom-dynamic-component";
import BaseBranding from "%/dynamic-component-editor/classes/branding/base-branding";
import StandardSurfaceSeeder from "%/dynamic-component-editor/classes/standard-surface-seeder";
import { renameEventInAnimationEffects } from "%/dynamic-component-editor/helpers/event-authoring";
import { payloadContextCandidates } from "%/dynamic-component-editor/helpers/payload-context-candidates";
import { getCompiledDcMeta } from "%/dynamic-components/render-engine/compiled-payload-store";
import { isCompiledRenderSurface } from "%/dynamic-component-editor/classes/platform-standard-entities";

// eslint-disable-next-line global-require
const useBuilderUndoStore = (...args) => require("%/dynamic-component-editor/stores/builder-undo-store").default(...args);
// eslint-disable-next-line global-require
const layoutElements = () => require("%/dynamic-component-editor/entities/LegacyLayoutEntity").default.elements;
// eslint-disable-next-line global-require
const createStoryMetadataElement = field => require("%/dynamic-component-editor/standard-components/base-story-card/create-story-metadata-element").createStoryMetadataElement(field);
// eslint-disable-next-line global-require
const standardPageClassBySlug = () => require("%/dynamic-component-editor/classes/standard-page-class-registry").STANDARD_PAGE_CLASS_BY_SLUG;

const CONTEXTS_ROOT_OPTION_KEYS = ["provides-contexts", "required-contexts"];
const RETIRED_CONTEXTS_SUFFIX = "-retired";


const ROOT_OPTIONS_SAVE_DEBOUNCE_MS = 400;

function resolveDynamicComponentClass(entityType, rootOptions) {
  const slug = rootOptions?.["page-slug"];
  const isDeclaredPage = entityType === "page" || entityType === "standard-page";
  const isCompiledPageWithoutEntity = !entityType && !!slug;
  if (!isDeclaredPage && !isCompiledPageWithoutEntity) {
    return CustomDynamicComponent;
  }
  return standardPageClassBySlug()[slug] ?? CustomDynamicPage;
}

class DynamicComponent extends BaseSingletonEntity {

  static #allowConstruction = false;

  static defaultThemeId = 0;

  static #TEMP_PK_MIN = 1e11;

  static #looksLikeTempPk(id) {
    const n = Number(id);
    return Number.isFinite(n) && n >= DynamicComponent.#TEMP_PK_MIN;
  }

  static #stripTempPkElementsFromReleaseConfig(config) {
    const elements = config?.elements;
    if (!elements || typeof elements !== "object") {
      return config;
    }
    const tempIds = new Set();
    for (const key of Object.keys(elements)) {
      if (DynamicComponent.#looksLikeTempPk(key)) {
        tempIds.add(String(key));
      }
    }
    if (tempIds.size === 0) {
      return config;
    }
    // eslint-disable-next-line no-console
    console.warn(
      `DynamicComponent: ${tempIds.size} temp-PK element(s) found in release config and filtered out. Republish to fix.`,
      { tempIds: Array.from(tempIds) },
    );
    const cleanedElements = {};
    for (const [id, element] of Object.entries(elements)) {
      if (tempIds.has(String(id))) {
        continue;
      }
      const childrenIds = element?.childrenIds;
      if (Array.isArray(childrenIds) && childrenIds.some(c => tempIds.has(String(c)))) {
        cleanedElements[id] = {
          ...element,
          childrenIds: childrenIds.filter(c => !tempIds.has(String(c))),
        };
      } else {
        cleanedElements[id] = element;
      }
    }
    return { ...config, elements: cleanedElements };
  }

  #stagingStore = null;
  #productionStore = null;
  #id = null;
  #isProduction = false;
  #registeredEventsKey = "registered-events";
  #rootOptionsSaveTimer = null;
  #rootOptionsTempPkListener = null;
  #variableBindingStamps = new Map();
  #variableBindingIndexBuilt = false;
  #variableBindingIndexVariables = null;
  #variableBindingIndexRootOptions = null;

  static get(id, isProduction = false) {
    if (id === null || id === undefined || id === "" || id === 0 || id === "0") {
      return null;
    }
    const instanceKey = isProduction
      ? `dynamic-component-${id}-production`
      : `dynamic-component-${id}-staging`;
    return BaseSingletonEntity._getInstance(this, instanceKey, () => {
      DynamicComponent.#allowConstruction = true;
      const instance = new this(id, isProduction);
      DynamicComponent.#allowConstruction = false;
      return instance;
    });
  }

  get #activeStore() {
    return this.#isProduction ? this.#productionStore : this.#stagingStore;
  }

  get isHydrated() {
    return !!this.#activeStore?.hydrated;
  }

  get version() {
    return this.#stagingStore?.currentVersion;
  }

  get rootElement() {
    return this.#stagingStore?.rootElement;
  }

  get rootElementId() {
    return this.#activeStore?.rootElementId;
  }

  get invalidCache() {
    return this.#stagingStore?.invalidCache;
  }

  get id() {
    return this.#id;
  }

  get canUndo() {
    return useBuilderUndoStore().canUndo(this.#id);
  }

  get canRedo() {
    return useBuilderUndoStore().canRedo(this.#id);
  }

  undo() {
    return useBuilderUndoStore().undo(this.#id);
  }

  redo() {
    return useBuilderUndoStore().redo(this.#id);
  }

  get destinationId() {
    if (isCompiledRenderSurface()) {
      const injected = window.blclient?.data?.themeDestinationId;
      return injected ? String(injected) : null;
    }
    const stagingId = this.#stagingStore?.entity?.destination?.pk?.id;
    if (stagingId !== undefined && stagingId !== null) {
      return String(stagingId);
    }
    const productionId = this.#productionStore?.entity?.destination?.pk?.id;
    if (productionId !== undefined && productionId !== null) {
      return String(productionId);
    }
    return null;
  }

  get type() {
    return this.#activeStore?.type;
  }

  get name() {
    return this.#activeStore?.name;
  }

  get storyType() {
    const stored = this.#activeStore?.rootOptions?.["story-type"];
    return typeof stored === "string" ? stored : "";
  }

  get isStoryTypePage() {
    return this.type === "page" && this.storyType !== "";
  }

  get variantGroupId() {
    return this.#activeStore?.variantGroupId ?? null;
  }

  get themeId() {
    const stored = this.#stagingStore?.themeId;
    if (stored && stored !== 0) {
      return stored;
    }
    return DynamicComponent.defaultThemeId || 0;
  }

  get themeMode() {
    if (this.#stagingStore?.themeMode) {
      return this.#stagingStore.themeMode;
    }
    return useStyleStore().getThemeMode(this.themeId);
  }

  get theme() {
    return Theme.get(this.themeId);
  }

  get breakpoints() {
    /* eslint-disable global-require */
    const useDynamicComponentEditorStoreModule = require("%/dynamic-component-editor/stores/dynamic-component-editor");
    /* eslint-enable global-require */
    const useDynamicComponentEditorStore = useDynamicComponentEditorStoreModule.default
      ?? useDynamicComponentEditorStoreModule.useDynamicComponentEditorStore;
    const fromEditorStore = useDynamicComponentEditorStore?.()?.availableBreakpoints ?? [];
    if (fromEditorStore.length) {
      return fromEditorStore;
    }
    const breakpointsFromDestination = this.#breakpointsFromDestinationConfig();
    if (breakpointsFromDestination.length) {
      return breakpointsFromDestination;
    }
    return this.#stagingStore?.breakpoints ?? [];
  }

  #breakpointsFromDestinationConfig() {
    if (isCompiledRenderSurface()) {
      /* eslint-disable global-require */
      const configModule = require("%/stores/configuration-store");
      /* eslint-enable global-require */
      const useConfigurationStore = configModule.useConfigurationStore ?? configModule.default;
      return useConfigurationStore?.()?.dynamicComponentBreakpoints ?? [];
    }
    const destinationId = this.destinationId;
    if (destinationId === null) {
      return [];
    }
    const studio = App.get("CreatorStudio");
    if (!studio) {
      return [];
    }
    const parent = studio.getEntity("Destination", { id: destinationId });
    if (!parent || parent.isEntityPkTemp?.()) {
      return [];
    }
    const pk = { id: destinationId };
    const feed = studio.getFeedStore("Destination", pk, "destinationConfiguration");
    feed?.triggerInitialLoad?.();
    const items = studio.getFeedItems("Destination", pk, "destinationConfiguration") ?? [];
    if (!items.length) {
      return [];
    }
    const configId = items[0].entityPk?.id;
    if (configId === undefined || configId === null) {
      return [];
    }
    const configEntity = studio.getEntity("DestinationConfiguration", { id: configId });
    return configEntity?.breakpoints ?? [];
  }

  get className() {
    return this.#activeStore?.className;
  }

  get class() {
    return this.#activeStore?.dynamicComponentClass;
  }

  get variables() {
    const stagingVars = this.#stagingStore?.variables;
    if (stagingVars && Object.keys(stagingVars).length > 0) {
      return stagingVars;
    }
    return this.#persistedVariables();
  }

  variableBindingStampFor(elementId) {
    const variables = this.#stagingStore?.variables;
    const rootOptions = this.#stagingStore?.entity?.rootOptions;
    if (!this.#variableBindingIndexBuilt
        || variables !== this.#variableBindingIndexVariables
        || rootOptions !== this.#variableBindingIndexRootOptions) {
      this.#variableBindingIndexVariables = variables;
      this.#variableBindingIndexRootOptions = rootOptions;
      this.#variableBindingIndexBuilt = true;
      this.#variableBindingStamps = buildVariableBindingIndex(this.#getVariablesMap());
    }
    return bindingStampFor(this.#variableBindingStamps, elementId);
  }

  #persistedVariables() {
    const raw = this.#stagingStore?.entity?.rootOptions ?? this.#stagingStore?.entity?.root_options;
    if (!raw) {
      return {};
    }
    try {
      const parsed = JSON.parse(raw);
      return parsed?.variables ?? {};
    } catch {
      return {};
    }
  }

  get rootOptions() {
    return this.#activeStore?.rootOptions;
  }

  get rootOptionsOverrides() {
    return this.#stagingStore.rootOptionsOverrides;
  }

  get rootOptionClasses() {
    if (!this.class) {
      return [];
    }
    const isStandard = this.isStandard;
    const LOCKED_STANDARD_OPTION_KEYS = new Set([
      "page-slug",
      "component-name",
      "display-name",
    ]);
    return (this.class.standardOptionClasses ?? []).map(entry => {
      const instance = typeof entry === "function"
        ? new entry()
        : Object.assign(Object.create(Object.getPrototypeOf(entry)), entry);
      if (isStandard && LOCKED_STANDARD_OPTION_KEYS.has(instance.optionKey)) {
        instance.disabled = true;
      }
      return instance;
    });
  }

  get availableContextsEntities() {
    const requiredContexts = this.getRootOption("required-contexts") ?? {};
    const providesContexts = this.getRootOption("provides-contexts") ?? {};
    if (!this.class) {
      return [];
    }
    const referredContexts = {};

    let computedContexts = [];
    Object.keys(providesContexts).forEach(key => {
      const providedContext = providesContexts[key];

      if (providedContext.referred) {
        referredContexts[key] = providedContext;
        return;
      }

      if (ProvidesContextEntry.isValueSource(providedContext) || ProvidesContextEntry.isPrimitive(providedContext)) {
        return;
      }

      computedContexts.push({
        name: key,
        entityType: providedContext.entityType,
        appName: providedContext.appName,
        pk: providedContext.entityPkUrlVars,
      });
    });

    Object.keys(requiredContexts).forEach(key => {
      const requiredContext = requiredContexts[key];

      if (requiredContext.referred) {
        referredContexts[key] = requiredContext;
        return;
      }

      if (isPrimitiveRequiredContext(requiredContext)) {
        return;
      }
      computedContexts.push({
        name: key,
        // Required contexts now store the entity under `entity` (the rework's
        // {entity} shape); tolerate the legacy `type` key too. Reading only
        // `.type` left entityType undefined for a {entity}-shaped required
        // context (e.g. currentUser {entity:"User"}), so its entity config never
        // resolved and it contributed no field elements to the palette.
        // XXX: BARE name. There are TWO registries with DIFFERENT keying and
        // XXX: they are easy to confuse: studio-legacy-entities-source (the
        // XXX: picker's display map) is keyed by the PREFIXED name
        // XXX: (LegacyStory), while blclient.data.legacyEntities and the staging
        // XXX: store's contextsEntities -- the FIELD-DEF registries read here --
        // XXX: are keyed BARE (Story). Using the prefixed key here silently
        // XXX: resolved no entity config, so a story card's StoryTitle /
        // XXX: StoryImage / StoryMetadataIcon all fell back to InvalidElement.
        // XXX: bareRequiredContextEntityType handles both stored shapes and
        // XXX: leaves non-legacy names (Recipe, User) untouched.
        entityType: bareRequiredContextEntityType(requiredContext),
        appName: requiredContext.appName,
      });
    });

    Object.keys(referredContexts).forEach(key => {
      let referredContext = referredContexts[key];

      while (referredContext.referred) {
        const foundReferredToContext = computedContexts.find(context => context.name === referredContext.referred);

        if (foundReferredToContext) {
          referredContext = foundReferredToContext;
        }
      }

      computedContexts.push({
        name: key,
        entityType: referredContext.entityType,
        appName: referredContext.appName,
      });
    });

    // XXX: The per-class static requiredContexts / providesContexts merge used
    // XXX: to live here and is gone: a DC's OWN contexts come from the stored
    // XXX: options only. Do not reintroduce a code-side declaration.
    // XXX:
    // XXX: STANDARD ELEMENTS -- layout / general / text -- are not contexts.
    // XXX: They bind to nothing and are always available on every DC. They ride
    // XXX: this list because it is what feeds the element registry and the
    // XXX: palette, but they are framework-supplied, not authored. Dropping
    // XXX: them emptied every standard group in the element panel.
    const standardElements = this.class?.getStandardElementEntities?.() ?? {};
    Object.keys(standardElements).forEach(key => {
      if (computedContexts.some(context => context.name === key)) {
        return;
      }
      computedContexts.push({ name: key, entityType: standardElements[key] });
    });


    const hostRoots = typeof window !== "undefined"
      ? window.blclient?.data?.hostStandardContextRoots ?? {}
      : {};
    Object.entries(hostRoots).forEach(([rootName, rootDef]) => {
      if (!rootDef?.entityType || computedContexts.some(context => context.name === rootName)) {
        return;
      }
      computedContexts.push({
        name: rootName,
        entityType: rootDef.entityType,
        hiddenFromElementsPanel: true,
      });
    });

    return computedContexts.map(contextItem => {
      const stagingConfig = contextItem.appName
        ? ContextSourceOptions.resolveEntityConfig(
          contextItem.entityType,
          contextItem.appName,
          Context.allSources,
          this.#stagingStore?.contextsEntities,
        )
        : this.#stagingStore?.contextsEntities?.[contextItem.entityType];
      const injected = typeof window !== "undefined" ? window.blclient?.data : null;
      let fallbackConfig = injected?.legacyEntities?.[contextItem.entityType] ?? null;
      if (!fallbackConfig && injected?.standardUserEntity?.entityType === contextItem.entityType) {
        fallbackConfig = injected.standardUserEntity;
      }
      const entityConfig = stagingConfig ?? fallbackConfig;
      return {
        ...contextItem,
        entity: BaseEntity.make(contextItem.name, entityConfig),
      };
    });
  }

  // Every element Vue component this DC can render, indexed by element
  // type name. Collected by walking every context entity the DC has
  // access to (same set the Elements panel uses to populate its groups)
  // and merging each entity's `getElements()` output.
  //
  // This is the single resolver the preview renderer should use — the
  // element panel in the editor and the rendered tree need to agree on
  // "what can this DC render", so they share the same source.
  // XXX: THE compile-time question: which element types will NOT resolve once
  // XXX: this DC is compiled? A context that exists ONLY in the builder's
  // XXX: resolved view -- injected from the code-side standard-surface list,
  // XXX: never stored in rootOptions -- does not reach the compiled payload, so
  // XXX: every element type it contributes silently falls back to
  // XXX: InvalidElement on staging while rendering perfectly in the builder.
  // XXX: That divergence is invisible to the author, so the layers panel flags
  // XXX: it from this set.
  get elementTypesLostOnCompile() {
    const lost = new Set();
    const builderOnly = this.#builderOnlyContextNames();
    if (builderOnly.size === 0) {
      return lost;
    }
    const survivorTypes = new Set(layoutElements()
        .map(element => element?.name)
        .filter(Boolean));
    this.availableContextsEntities.forEach(contextItem => {
      if (builderOnly.has(contextItem?.name)) {
        return;
      }
      const entity = contextItem?.entity;
      if (!entity || typeof entity.getElements !== "function") {
        return;
      }
      (entity.getElements() ?? []).forEach(element => {
        if (element?.name) {
          survivorTypes.add(element.name);
        }
      });
    });
    Object.keys(this.availableElementVueComponents).forEach(type => {
      if (!survivorTypes.has(type)) {
        lost.add(type);
      }
    });
    return lost;
  }

  #builderOnlyContextNames() {
    const names = new Set();
    CONTEXTS_ROOT_OPTION_KEYS.forEach(optionKey => {
      const stored = this.#activeStore?.rootOptions?.[optionKey];
      const storedNames = stored && typeof stored === "object" && !Array.isArray(stored)
        ? Object.keys(stored)
        : [];
      const resolved = this.getRootOption(optionKey) ?? {};
      Object.keys(resolved).forEach(name => {
        if (!storedNames.includes(name)) {
          names.add(name);
        }
      });
    });
    return names;
  }

  get pageClassOnlyElements() {
    const dynamicComponentClass = this.class;
    if (!dynamicComponentClass || typeof dynamicComponentClass.getPossibleElements !== "function") {
      return [];
    }

    const ownNames = new Set(
      (dynamicComponentClass.possibleElementVueComponents ?? [])
          .map(element => element?.name)
          .filter(Boolean),
    );
    if (ownNames.size === 0) {
      return [];
    }

    const contributed = new Set();
    this.availableContextsEntities.forEach(contextItem => {
      let elements = [];
      try {
        elements = contextItem.entity?.getElements?.() ?? [];
      } catch {
        elements = [];
      }
      elements.forEach(element => element?.name && contributed.add(element.name));
    });

    let processed = [];
    try {
      processed = dynamicComponentClass.getPossibleElements();
    } catch {
      return [];
    }

    return processed.filter(element => element?.name
      && ownNames.has(element.name)
      && !contributed.has(element.name)
      && !element.deprecated
      && !element.hidden);
  }

  get availableElementVueComponents() {
    const map = {};
    const allElementsFlattened = [];
    for (const element of layoutElements()) {
      if (element?.name) {
        map[element.name] = element;
        allElementsFlattened.push(element);
      }
    }
    this.availableContextsEntities.forEach(contextItem => {
      const entity = contextItem.entity;
      if (!entity || typeof entity.getElements !== "function") {
        return;
      }
      let elements;
      try {
        elements = entity.getElements();
      } catch {
        return;
      }
      if (!Array.isArray(elements)) {
        return;
      }
      for (const element of elements) {
        if (element?.name) {
          map[element.name] = element;
          allElementsFlattened.push(element);
        }
      }
    });
    const dcClass = this.class;
    if (dcClass && typeof dcClass.getPossibleElements === "function") {
      let classElements;
      try {
        classElements = dcClass.getPossibleElements();
      } catch {
        classElements = null;
      }
      if (Array.isArray(classElements)) {
        for (const element of classElements) {
          if (element?.name) {
            map[element.name] = element;
            allElementsFlattened.push(element);
          }
        }
      }
    }
    if (map.StoryTitle) {
      for (const field of allMetadataFields()) {
        const metadataElement = createStoryMetadataElement(field);
        map[metadataElement.name] = metadataElement;
        allElementsFlattened.push(metadataElement);
      }
    }
    DynamicElement.registerElementDefinitions(allElementsFlattened);

    return map;
  }

  resolveStoryTypeForMetadata() {
    const explicit = this.getRootOption("story-type");
    if (explicit && STORY_TYPE_METADATA_FIELDS[explicit]) {
      return explicit;
    }
    const label = String(
      this.getRootOption("display-name") ?? this.getRootOption("component-name") ?? "",
    ).toLowerCase();
    return Object.keys(STORY_TYPE_METADATA_FIELDS).find(type => label.startsWith(type)) ?? null;
  }

  getElementVueComponent(elementType, forVisitorSurface = false) {
    if (!elementType) {
      return null;
    }
    const resolved = this.availableElementVueComponents[elementType] ?? null;
    if (!forVisitorSurface || !resolved) {
      return resolved;
    }

    return this.class?.baseProductionElements?.[elementType] ?? resolved;
  }

  set version(versionId) {
    this.#stagingStore.currentVersion = versionId;
  }

  static #storyMetadataElementsRegistered = false;

  static #registerStoryMetadataElements() {
    if (DynamicComponent.#storyMetadataElementsRegistered) {
      return;
    }
    DynamicComponent.#storyMetadataElementsRegistered = true;
    DynamicElement.registerElementDefinitions(allMetadataFields().map(createStoryMetadataElement));
  }

  constructor(id, isProduction = false) {
    super();
    if (!DynamicComponent.#allowConstruction) {
      throw new DynamicComponentError("DynamicComponent constructor is private. Use DynamicComponent.get() instead.", {});
    }
    DynamicComponent.#registerStoryMetadataElements();
    this.#id = id;
    this.#isProduction = isProduction;

    if (isProduction) {
      this.#productionStore = this.#initStore("production");
    } else {
      this.#stagingStore = this.#initStore("staging");
    }
  }

  get isProduction() {
    return this.#isProduction;
  }

  getElement(elementId) {
    if (this.#isProduction) {
      return this.#productionStore.getElement(elementId);
    }
    const de = DynamicElement.get(elementId);
    if (!de?.element) {
      return null;
    }
    return {
      type: de.type,
      options: de.parsedOptions,
      childrenIds: de.childElementIds,
    };
  }

  getThemeElementFor(type, variant, size) {
    if (this.#isProduction) {
      const themeElementMap = this.#productionStore?.parsedReleaseConfiguration?.themeElements ?? null;
      if (!themeElementMap) {
        return null;
      }
      return themeElementMap[this.#themeElementKey(type, variant, size)] ?? null;
    }
    const editorDestinationId = this.#resolveActiveEditorDestinationId();
    const destinationId = editorDestinationId ?? this.destinationId;
    if (destinationId === null) {
      return null;
    }
    return BaseBranding.findThemeElementFor(destinationId, type, variant, size);
  }

  #resolveActiveEditorDestinationId() {
    const activeThemeId = this.#stagingStore?.themeId;
    if (!activeThemeId) {
      return null;
    }
    const studioApp = App.get("CreatorStudio");
    if (!studioApp) {
      return null;
    }
    const theme = studioApp.getEntity?.("Theme", { id: activeThemeId });
    const destId = theme?.destination?.pk?.id ?? null;
    if (destId === null || destId === undefined) {
      return null;
    }
    return String(destId);
  }

  #themeElementKey(type, variant, size) {
    return `${type ?? ""}|${variant ?? ""}|${size ?? ""}`;
  }

  updateContextsEntities(entities) {
    if (this.#stagingStore) {
      this.#stagingStore.contextsEntities = entities;
    }
  }

  setup(themeId, themeMode, breakpoints, entities) {
    if (this.#stagingStore) {
      this.#stagingStore.themeId = themeId;
      this.#stagingStore.themeMode = themeMode;
      this.#stagingStore.breakpoints = breakpoints;
      this.#stagingStore.contextsEntities = entities;
      applyThemeMotionForThemeId(themeId);
    }
    watch(
      () => this.availableContextsEntities,
      contextItems => {
        const allElements = [];
        (contextItems ?? []).forEach(contextItem => {
          const entity = contextItem?.entity;
          if (!entity || typeof entity.getElements !== "function") {
            return;
          }
          let elements;
          try {
            elements = entity.getElements();
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
              `registerElementDefinitions: ${contextItem?.name ?? "unknown"} context entity's getElements() threw — its elements will be missing from the registry`,
              error,
            );
            return;
          }
          if (Array.isArray(elements)) {
            allElements.push(...elements);
          }
        });
        DynamicElement.registerElementDefinitions(allElements);
      },
      { immediate: true, deep: true },
    );

    if (this.#stagingStore && !isCompiledRenderSurface()) {
      const destinationId = this.destinationId;
      if (destinationId !== null && destinationId !== undefined && destinationId !== "") {
        App.get("CreatorStudio")
            .getFeedStore("Destination", { id: String(destinationId) }, "themeElements")
            .triggerInitialLoad?.();
      }
    }
  }

  addBreakpoint(breakpoint) {
    this.#stagingStore.breakpoints.push(breakpoint);
  }

  updateThemeMode(themeMode) {
    this.#stagingStore.themeMode = themeMode;
  }

  getRootOption(optionKey) {
    const stored = this.#activeStore?.rootOptions?.[optionKey];
    if (stored !== undefined) {
      if (CONTEXTS_ROOT_OPTION_KEYS.includes(optionKey)) {
        return this.#withStandardSurfaceContexts(optionKey, stored);
      }
      return stored;
    }
    if (CONTEXTS_ROOT_OPTION_KEYS.includes(optionKey)) {
      const canonicalOnly = this.#liveStandardSurfaceContexts(optionKey);
      if (canonicalOnly !== null) {
        return canonicalOnly;
      }
    }
    const optionClass = this.getRootOptionClass(optionKey);
    if (!optionClass) {
      return undefined;
    }
    return this.#defaultValueForRootOption(optionClass);
  }

  #liveStandardSurfaceContexts(optionKey) {
    const canonical = StandardSurfaceSeeder.standardContextsForClass(this.standardSurfaceKey, optionKey);
    if (!canonical) {
      return null;
    }
    const retired = this.#retiredStandardSurfaceContexts(optionKey);
    const live = {};
    Object.keys(canonical).forEach(contextName => {
      if (!retired.has(contextName)) {
        live[contextName] = canonical[contextName];
      }
    });
    return Object.keys(live).length === 0 ? null : live;
  }

  #retiredStandardSurfaceContexts(optionKey) {
    const raw = this.#activeStore?.rootOptions?.[`${optionKey}${RETIRED_CONTEXTS_SUFFIX}`];
    if (typeof raw !== "string" || raw === "") {
      return new Set();
    }
    return new Set(raw.split(",").map(name => name.trim()).filter(Boolean));
  }

  #withStandardSurfaceContexts(optionKey, authored) {
    const live = this.#liveStandardSurfaceContexts(optionKey);
    if (live === null) {
      return authored;
    }
    return { ...live, ...authored ?? {} };
  }

  retireStandardContext(optionKey, contextName) {
    const canonical = StandardSurfaceSeeder.standardContextsForClass(this.standardSurfaceKey, optionKey);
    if (!canonical || canonical[contextName] === undefined) {
      return;
    }
    const retired = this.#retiredStandardSurfaceContexts(optionKey);
    retired.add(contextName);
    this.modifyRootOption(`${optionKey}${RETIRED_CONTEXTS_SUFFIX}`, [...retired].join(","));
  }

  getRootOptionClass(optionKey) {
    return this.rootOptionClasses.find(instance => instance.optionKey === optionKey) ?? null;
  }

  getRootOptionTools() {
    return this.rootOptionClasses.filter(instance => !instance.shouldHideFromInspector);
  }

  modifyRootOption(optionKey, value) {
    this.#stagingStore.rootOptionsOverrides[optionKey] = value;
    this.#syncRootOptions();
  }


  #getVariablesMap() {
    const stagingVars = this.#stagingStore?.variables ?? {};
    if (Object.keys(stagingVars).length > 0) {
      return stagingVars;
    }
    return this.#persistedVariables();
  }

  #writeVariablesMap(variables) {
    if (!this.#stagingStore) {
      return;
    }
    this.#stagingStore.variables = variables;
    this.#syncRootOptions();
  }

  static #linkKey(elementId, optionKey) {
    return `${String(elementId).split(":")[0]}:${optionKey}`;
  }

  getVariableValue(variableIdOrElementId, variableId) {
    const key = variableId !== undefined ? variableId : variableIdOrElementId;
    const variable = this.#getVariablesMap()[key];
    if (!variable) {
      return undefined;
    }
    return variable.default;
  }

  getVariableBindingForOption(elementId, optionKey) {
    const lookup = DynamicComponent.#linkKey(elementId, optionKey);
    const variables = this.#getVariablesMap();
    for (const variableId of Object.keys(variables)) {
      if (variables[variableId]?.linked?.[lookup]) {
        return variableId;
      }
    }
    return null;
  }

  createVariable(label, optionClass) {
    const variableType = optionClass.constructor.name;
    const variables = { ...this.#getVariablesMap() };
    variables[String(Date.now())] = {
      label,
      type: variableType,
      optionKey: optionClass.optionKey,
      default: undefined,
      linked: {},
    };
    this.#writeVariablesMap(variables);
  }

  removeVariable(variableId) {
    const variables = { ...this.#getVariablesMap() };
    delete variables[variableId];
    this.#writeVariablesMap(variables);
  }

  linkVariable(variableId, elementId, optionKey, currentValue) {
    const variables = { ...this.#getVariablesMap() };
    const existing = variables[variableId] ?? { label: "", type: "", optionKey, default: undefined, linked: {} };
    const linked = { ...existing.linked ?? {} };
    linked[DynamicComponent.#linkKey(elementId, optionKey)] = true;
    variables[variableId] = {
      ...existing,
      default: existing.default === undefined ? currentValue : existing.default,
      linked,
    };
    this.#writeVariablesMap(variables);
  }

  unlinkVariable(variableId, elementId, optionKey) {
    const variables = { ...this.#getVariablesMap() };
    const existing = variables[variableId];
    if (!existing) {
      return;
    }
    const linked = { ...existing.linked ?? {} };
    delete linked[DynamicComponent.#linkKey(elementId, optionKey)];
    variables[variableId] = { ...existing, linked };
    this.#writeVariablesMap(variables);
  }

  updateVariableLabel(variableId, label) {
    const variables = { ...this.#getVariablesMap() };
    if (!variables[variableId]) {
      return;
    }
    variables[variableId] = { ...variables[variableId], label };
    this.#writeVariablesMap(variables);
  }

  updateVariableDefaultValue(variableId, value) {
    const variables = { ...this.#getVariablesMap() };
    if (!variables[variableId]) {
      return;
    }
    const raw = value && typeof value === "object" && "value" in value ? value.value : value;
    variables[variableId] = { ...variables[variableId], default: raw };
    this.#writeVariablesMap(variables);
  }

  changeVariableOperator(/* variableId, elementTemplateId */) {
  }

  modifyVariableValue(/* elementId, variableKey, optionKey, value */) {
  }

  registerEvent(newEvent) {
    let registeredEvents = this.getRootOption(this.#registeredEventsKey);
    if (!registeredEvents) {
      this.modifyRootOption(this.#registeredEventsKey, {});
      registeredEvents = this.getRootOption(this.#registeredEventsKey);
    }
    if (registeredEvents[newEvent.name]) {
      return;
    }
    registeredEvents[newEvent.name] = newEvent;
    this.modifyRootOption(this.#registeredEventsKey, registeredEvents);
  }

  unregisterEvent(eventName) {
    const registeredEvents = this.getRootOption(this.#registeredEventsKey);
    if (!registeredEvents || !registeredEvents[eventName]) {
      // eslint-disable-next-line no-console
      console.error(
        `[DynamicComponent#${this.#id}] unregisterEvent("${eventName}") dropped — the event is not in the `
        + `registered-events registry (${JSON.stringify(Object.keys(registeredEvents || {}))}). A stale `
        + "rootOptionsOverrides layer can mask the server registry; the registry entry was NOT removed.",
      );
      return;
    }
    delete registeredEvents[eventName];
    this.modifyRootOption(this.#registeredEventsKey, registeredEvents);
  }

  updateEvent(existingEventName, newEventName) {
    const registeredEvents = this.getRootOption(this.#registeredEventsKey);
    if (!registeredEvents || !registeredEvents[existingEventName]) {
      // eslint-disable-next-line no-console
      console.error(
        `[DynamicComponent#${this.#id}] updateEvent("${existingEventName}" → "${newEventName}") dropped — the `
        + `event is not in the registered-events registry (${JSON.stringify(Object.keys(registeredEvents || {}))}). `
        + "A stale rootOptionsOverrides layer can mask the server registry; NO rename or reference sweep ran.",
      );
      return;
    }
    const existingEvent = JSON.parse(JSON.stringify(registeredEvents[existingEventName]));
    delete registeredEvents[existingEventName];
    existingEvent.name = newEventName;
    registeredEvents[newEventName] = existingEvent;
    this.modifyRootOption(this.#registeredEventsKey, registeredEvents);
    this.#sweepRenamedEventReferences(existingEventName, newEventName);
  }

  #sweepRenamedEventReferences(oldName, newName) {
    const stack = [this.rootElementId].filter(id => id !== null && id !== undefined);
    while (stack.length) {
      const dynamicElement = DynamicElement.get(stack.pop());
      if (!dynamicElement) {
        continue;
      }
      (dynamicElement.childElementIds ?? []).forEach(childId => stack.push(childId));

      const rawOptions = String(dynamicElement.element?.options ?? "");
      const referencesOldName = rawOptions.includes(oldName);
      if (!referencesOldName) {
        continue;
      }
      const modeTree = dynamicElement.parsedOptions?.["light-mode"] ?? {};
      Object.entries(modeTree).forEach(([breakpoint, states]) => {
        Object.entries(states ?? {}).forEach(([state, options]) => {
          const renamed = renameEventInAnimationEffects(
            options?.["animation-effects"]?.r, oldName, newName,
          );
          if (renamed) {
            dynamicElement.updateOption("animation-effects", breakpoint, state, renamed);
          }
        });
      });
    }
  }

  getEvent(eventName) {
    const registeredEvents = this.getRootOption(this.#registeredEventsKey);
    return registeredEvents?.[eventName];
  }

  getAvailableEvents() {
    return this.getRootOption(this.#registeredEventsKey);
  }

  mergeInAddedElementEventsToParentRootOptions(elementType) {
    const events = this.#relatedComponentEventsForElementType(elementType);
    if (!events) {
      return;
    }
    Object.values(events).forEach(event => {
      this.registerEvent(event);
    });
  }

  removeRemovedElementEventsFromParentRootOptions(elementType) {
    const events = this.#relatedComponentEventsForElementType(elementType);
    if (!events) {
      return;
    }
    Object.keys(events).forEach(eventName => {
      this.unregisterEvent(eventName);
    });
  }

  /** ******************* Inspector Stubs — TODO: real impl *********************/
  reprocessVariablesAndElements() {}
  switchTemplateForElement() {}


  _initializeStore() {
    throw new DynamicComponentError("DynamicComponent manages its own stores via #initStore()", {});
  }

  #initStore(environment) {
    if (environment === "production") {
      return this.#initProductionStore();
    }
    return this.#initStagingStore();
  }

  #initStagingStore() {
    const storeKey = `dynamic-component-${this.#id}-staging`;
    const id = this.#id;

    const store = this._defineStore(storeKey, {
      state: () => ({
        cacheVersion: 0,
        currentVersion: 0,
        themeMode: "light-mode",
        themeId: 0,
        contextsEntities: {},
        breakpoints: [],
        variables: {},
        invalidCache: false,
        rootOptionsOverrides: {},
      }),
      getters: {
        entity() {
          if (id === null || id === undefined || id === "" || id === 0 || id === "0") {
            return null;
          }
          if (isCompiledRenderSurface()) {
            return null;
          }
          const app = App.get("CreatorStudio");
          return app.getEntity("DynamicComponent", { id });
        },
        hydrated() {
          if (isCompiledRenderSurface()) {
            return !!getCompiledDcMeta(id);
          }
          return this.entity?.isHydrated;
        },
        name() {
          return this.entity?.name ?? "";
        },
        type() {
          return this.entity?.type ?? "";
        },
        variantGroupId() {
          return this.entity?.variantGroupId ?? null;
        },
        className() {
          const t = this.entity?.type;
          return t === "page" || t === "standard-page" ? "CustomDynamicPage" : "CustomDynamicComponent";
        },
        dynamicComponentClass() {
          return resolveDynamicComponentClass(this.entity?.type, this.rootOptions);
        },
        rootElement() {
          const rootElementId = this.rootElementId;
          return rootElementId ? DynamicElement.get(rootElementId).element : null;
        },
        rootElementId() {
          if (isCompiledRenderSurface()) {
            return getCompiledDcMeta(id)?.rootElementId ?? null;
          }
          return this.entity?.rootElement?.pk?.id ?? null;
        },
        rootOptions() {
          const entityOptions = isCompiledRenderSurface()
            ? getCompiledDcMeta(id)?.rootOptions ?? {}
            : JSON.parse(this.entity?.rootOptions ?? this.entity?.root_options ?? "{}");
          return {
            ...entityOptions,
            ...this.rootOptionsOverrides,
            variables: this.variables,
          };
        },
      },
    });

    return store;
  }

  #initProductionStore() {
    const storeKey = `dynamic-component-${this.#id}-production`;
    const id = this.#id;

    return this._defineStore(storeKey, {
      state: () => ({}),
      getters: {
        entity() {
          if (id === null || id === undefined || id === "" || isCompiledRenderSurface()) {
            return null;
          }
          return App.get("CreatorStudio").getEntity("DynamicComponent", { id });
        },
        release() {
          const releaseId = this.entity?.release?.pk?.id ?? null;
          if (!releaseId) {
            return null;
          }
          return App.get("CreatorStudio").getEntity("DynamicComponentRelease", { id: releaseId });
        },
        parsedReleaseConfiguration() {
          const configuration = this.release?.configuration;
          if (!configuration) {
            return {};
          }
          let parsed;
          try {
            parsed = JSON.parse(configuration);
          } catch {
            return {};
          }
          return DynamicComponent.#stripTempPkElementsFromReleaseConfig(parsed);
        },
        releaseId() {
          return this.release?.pk?.id ?? null;
        },
        rootElementId() {
          const rootId = isCompiledRenderSurface()
            ? getCompiledDcMeta(id)?.rootElementId ?? null
            : this.parsedReleaseConfiguration.rootElementId ?? null;
          if (rootId !== null && DynamicComponent.#looksLikeTempPk(rootId)) {
            return null;
          }
          return rootId;
        },
        elements() {
          return this.parsedReleaseConfiguration.elements ?? {};
        },
        rootOptions() {
          if (isCompiledRenderSurface()) {
            return getCompiledDcMeta(id)?.rootOptions ?? {};
          }
          return this.parsedReleaseConfiguration.rootOptions ?? {};
        },
        hydrated() {
          if (isCompiledRenderSurface()) {
            return !!getCompiledDcMeta(id);
          }
          return this.entity?.isHydrated;
        },
        name() {
          return this.entity?.name ?? "";
        },
        type() {
          return this.entity?.type ?? "";
        },
        variantGroupId() {
          return this.entity?.variantGroupId ?? null;
        },
        className() {
          const t = this.entity?.type;
          return t === "page" || t === "standard-page" ? "CustomDynamicPage" : "CustomDynamicComponent";
        },
        dynamicComponentClass() {
          return resolveDynamicComponentClass(this.entity?.type, this.rootOptions);
        },
        getElement() {
          return elementId => this.elements?.[elementId] ?? null;
        },
      },
    });
  }

  get isStandard() {
    const stored = this.#activeStore?.isStandard;
    if (typeof stored === "boolean") {
      return stored;
    }
    if (stored === "1" || stored === 1) {
      return true;
    }
    return this.standardSurfaceKey !== "";
  }

  get standardSurfaceKey() {
    const stored = this.#activeStore?.rootOptions?.["standard-surface-key"];
    if (typeof stored === "string" && stored !== "") {
      return stored;
    }
    const slug = this.#activeStore?.rootOptions?.["page-slug"];
    const name = this.name;
    if (typeof name === "string" && name !== "") {
      return StandardSurfaceSeeder.standardSurfaceKeyForNameAndSlug(name, slug);
    }
    return StandardSurfaceSeeder.standardSurfaceKeyForSlug(slug);
  }

  #syncRootOptions() {
    if (this.#rootOptionsSaveTimer) {
      clearTimeout(this.#rootOptionsSaveTimer);
    }
    this.#rootOptionsSaveTimer = setTimeout(() => {
      this.#rootOptionsSaveTimer = null;
      this.#sendRootOptionsNow();
    }, ROOT_OPTIONS_SAVE_DEBOUNCE_MS);
  }

  flushRootOptionsSaveNow() {
    if (!this.#rootOptionsSaveTimer) {
      return;
    }
    clearTimeout(this.#rootOptionsSaveTimer);
    this.#rootOptionsSaveTimer = null;
    this.#sendRootOptionsNow();
  }

  #sendRootOptionsNow() {
    const app = App.get("CreatorStudio");
    const entity = app.getEntity("DynamicComponent", { id: this.#id });
    const resolvedPk = entity?.getPk?.() ?? { id: this.#id };
    if (entity?.isEntityPkTemp?.()) {
      this.#queueRootOptionsUntilTempPkResolves();
      return;
    }
    app.triggerEvent("UpdateDynamicComponentRootOptions", {
      dynamicComponent: resolvedPk,
      rootOptions: JSON.stringify(this.rootOptions),
    });
  }

  #queueRootOptionsUntilTempPkResolves() {
    if (this.#rootOptionsTempPkListener || typeof window === "undefined") {
      return;
    }
    const tempPkString = App.getPkString({ id: this.#id });
    this.#rootOptionsTempPkListener = event => {
      const detail = event?.detail ?? {};
      if (detail.appName !== "CreatorStudio"
        || detail.entityType !== "DynamicComponent"
        || detail.tempPkString !== tempPkString) {
        return;
      }
      window.removeEventListener(TEMP_PK_RESOLVED_EVENT, this.#rootOptionsTempPkListener);
      this.#rootOptionsTempPkListener = null;
      App.get("CreatorStudio").triggerEvent("UpdateDynamicComponentRootOptions", {
        dynamicComponent: { ...detail.realPk },
        rootOptions: JSON.stringify(this.rootOptions),
      });
    };
    window.addEventListener(TEMP_PK_RESOLVED_EVENT, this.#rootOptionsTempPkListener);
  }

  #defaultValueForRootOption(instance) {
    const key = instance.optionKey;
    const nameBackedKeys = new Set(["component-name", "display-name", "page-title"]);
    const entityName = this.entity?.name;
    if (nameBackedKeys.has(key) && entityName) {
      return entityName;
    }
    return instance.constructor?.defaultValue ?? instance.defaultValue;
  }


  collectLiveEventNames(excludedElementIds = new Set()) {
    const collected = { liveNames: new Set(), emitterByName: {}, listenersByName: {}, visitedCount: 0 };
    const excluded = new Set([...excludedElementIds].map(String));
    const stack = [this.rootElementId].filter(id => id !== null && id !== undefined);

    while (stack.length) {
      const elementId = stack.pop();
      if (excluded.has(String(elementId))) {
        continue;
      }
      const dynamicElement = DynamicElement.get(elementId);
      if (!dynamicElement) {
        continue;
      }
      collected.visitedCount++;
      (dynamicElement.childElementIds ?? []).forEach(childId => stack.push(childId));
      this.#collectRelatedComponentEvents(dynamicElement, collected);
      this.#collectEmittedEvents(dynamicElement, elementId, collected);
    }
    return collected;
  }

  #collectRelatedComponentEvents(dynamicElement, collected) {
    const relatedEvents = this.#relatedComponentEventsForElementType(dynamicElement.element?.type);
    Object.keys(relatedEvents ?? {}).forEach(name => collected.liveNames.add(name));
  }

  #collectEmittedEvents(dynamicElement, elementId, collected) {
    const raw = dynamicElement.element?.options;
    const rawOptions = typeof raw === "string" ? raw : JSON.stringify(raw ?? "");
    if (!rawOptions.includes(TRIGGER_EVENT_ID) && !rawOptions.includes(`"${TRIGGER_ON_EVENT}"`)) {
      return;
    }
    const modeTree = dynamicElement.parsedOptions?.["light-mode"] ?? {};
    Object.values(modeTree).forEach(states => {
      Object.values(states ?? {}).forEach(options => {
        this.#collectEmittersFromEffects(options?.["animation-effects"]?.r, elementId, collected);
      });
    });
  }

  #collectEmittersFromEffects(effects, elementId, collected) {
    if (!effects || typeof effects !== "object") {
      return;
    }
    Object.entries(effects).forEach(([trigger, entries]) => {
      (Array.isArray(entries) ? entries : []).forEach(entry => {
        // Listeners feed the effects panel's "who reacts"; liveNames stays emitters-only.
        if (trigger === TRIGGER_ON_EVENT && typeof entry?.event === "string" && entry.event !== "") {
          const listeners = collected.listenersByName[entry.event] ??= [];
          if (!listeners.includes(String(elementId))) {
            listeners.push(String(elementId));
          }
          return;
        }
        const emittedName = entry?.id === TRIGGER_EVENT_ID ? entry[TRIGGERED_EVENT_KEY] : null;
        if (!emittedName) {
          return;
        }
        collected.liveNames.add(emittedName);
        collected.emitterByName[emittedName] ??= {
          source: String(elementId),
          actionType: trigger,
          scope: entry.scope === "instance" ? "instance" : "page",
          payload: Array.isArray(entry.payload) ? entry.payload : [],
        };
      });
    });
  }

  payloadCandidatesForEvent(eventName) {
    if (!eventName) {
      return [];
    }
    const { emitterByName } = this.collectLiveEventNames();
    return payloadContextCandidates(emitterByName[String(eventName)]?.payload);
  }

  reconcileRegisteredEvents(deletedElementIds = []) {
    const { liveNames, emitterByName, visitedCount } = this.collectLiveEventNames(new Set(deletedElementIds));
    if (visitedCount === 0) {
      return;
    }
    const registry = this.getAvailableEvents() ?? {};
    Object.keys(registry).forEach(name => {
      if (!liveNames.has(name)) {
        this.unregisterEvent(name);
      }
    });
    Object.entries(emitterByName).forEach(([name, emitter]) => {
      if (!registry[name]) {
        this.registerEvent({ name, source: emitter.source, actionType: emitter.actionType });
      }
    });
  }

  #relatedComponentEventsForElementType(elementType) {
    const elementDef = DynamicElement.getElementDefinition(elementType);
    if (!elementDef?.relatedComponents?.length) {
      return null;
    }
    const relatedComponentName = elementDef.relatedComponents[0];
    const useDynamicComponentStore = require("%/dynamic-component-editor/stores/dynamic-component-store").default;
    const relatedComponentConfig = useDynamicComponentStore().getDynamicComponentConfigByName(relatedComponentName);
    return relatedComponentConfig?.rootOptions?.["registered-events"] ?? null;
  }
}


export default DynamicComponent;

